import React from 'react';
import { CheckCircle, Award, Shield, Clock } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

export default function AboutNew() {
  const stats = [
    { value: '30+', label: 'Années d\'expérience', icon: Clock },
    { value: '15,000+', label: 'Couples réunis', icon: Award },
    { value: '4.9/5', label: 'Satisfaction', icon: CheckCircle },
    { value: '7j', label: 'Résultats garantis', icon: Shield },
  ];

  const guarantees = [
    'Confidentialité totale garantie',
    'Résultats visibles en 7 jours',
    'Approche personnalisée',
    'Première consultation découverte'
  ];

  return (
    <section id="about" className="py-12 bg-black relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-yellow-500/10 border-yellow-500/30 text-yellow-500">
            Spécialiste Retour Affectif
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Votre Expert Spirituel
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Image and Profile */}
          <div>
            <Card className="p-8 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30">
              <div className="flex flex-col items-center text-center space-y-6">
                {/* Image placeholder */}
                <div className="relative">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-4 border-yellow-500/30 flex items-center justify-center">
                    <span className="text-6xl">🔮</span>
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center border-4 border-black">
                    <CheckCircle className="h-5 w-5 text-black" />
                  </div>
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-xs">
                    30 ans d'exp.
                  </Badge>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-yellow-500 mb-1">Maître Diamma</h3>
                  <p className="text-gray-400 mb-2">Grand Marabout Africain<span className="seo-hidden"> Marseille</span></p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 justify-center">
                  <Badge variant="outline" className="border-yellow-500/30 text-yellow-500">Retour Affectif</Badge>
                  <Badge variant="outline" className="border-yellow-500/30 text-yellow-500">Voyance</Badge>
                  <Badge variant="outline" className="border-yellow-500/30 text-yellow-500">Protection</Badge>
                </div>

                <Button asChild size="lg" className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                  <a href="tel:+33745449641">07 45 44 96 41</a>
                </Button>
              </div>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              {stats.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <Card key={idx} className="p-4 text-center bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 hover:border-yellow-500/40 transition-all">
                    <Icon className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-yellow-500 mb-1">{stat.value}</p>
                    <p className="text-xs text-gray-400">{stat.label}</p>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Right: Biography */}
          <div className="space-y-6">
            <div className="prose prose-invert max-w-none">
              <p className="text-base text-gray-300 leading-relaxed">
                Bienvenue. Je m'appelle Dia. Depuis mon enfance au Sénégal, j'ai reçu le <strong className="text-yellow-500">don de percevoir</strong> ce que d'autres ne voient pas. Mon grand-père, guérisseur respecté dans notre village, m'a transmis ses connaissances des <strong className="text-yellow-500">rituels ancestraux</strong>.
              </p>
              
              <p className="text-base text-gray-300 leading-relaxed">
                J'ai consacré ma vie à <strong className="text-yellow-500">réunir les couples</strong>. Plus de <strong className="text-yellow-500">15,000 personnes</strong> m'ont fait confiance pour leur <strong className="text-yellow-500">retour affectif</strong>. Aujourd'hui, je peux vous aider aussi.
              </p>
            </div>

            {/* Why Trust Section */}
            <div className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 border border-yellow-500/20 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-4">Pourquoi me faire confiance ?</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Je ne fais pas de promesses en l'air. Quand vous me consultez, je vous dis la <strong className="text-yellow-500">vérité</strong> — même si ce n'est pas ce que vous voulez entendre. <strong className="text-yellow-500">Mon travail est basé sur l'honnêteté</strong>.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Je comprends que vous avez peut-être été déçu par d'autres voyants avant. C'est pourquoi la <strong className="text-yellow-500">première consultation est une consultation découverte</strong> — pour que vous puissiez juger par vous-même.
              </p>
            </div>

            {/* Guarantees */}
            <div className="grid sm:grid-cols-2 gap-4">
              {guarantees.map((guarantee, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-gray-900/50 border border-yellow-500/20 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-300">{guarantee}</p>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div className="border-l-4 border-yellow-500 pl-6 py-4">
              <p className="text-lg italic text-gray-300">
                "Je ne croyais pas à la voyance avant. Aujourd'hui, mon ex est revenu et nous sommes plus heureux que jamais. Merci infiniment."
              </p>
              <p className="text-sm text-gray-500 mt-2">— Sophie M.</p>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-xl p-6 text-center">
              <h4 className="text-xl font-bold text-white mb-3">Prêt à récupérer votre ex ?</h4>
              <p className="text-gray-400 mb-4">Première consultation <strong className="text-yellow-500">DÉCOUVERTE</strong> et sans engagement.</p>
              <Button asChild size="lg" className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                <a href="tel:+33745449641">Appeler maintenant</a>
              </Button>
              <p className="text-xs text-gray-500 mt-3">Disponible 7j/7 • 8h - 23h30</p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center max-w-3xl mx-auto">
          <p className="text-gray-400 mb-4">
            Plus de <strong className="text-yellow-500">15,000 couples réunis</strong> en 30 ans
          </p>
          <Button asChild variant="outline" size="lg" className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10">
            <a href="#temoignages">Lire les témoignages →</a>
          </Button>
        </div>
      </div>
    </section>
  );
}
